import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MunicipalityStoreConfig } from '@app/municipality-management/models/municipality-management';
import { MunicipalityManagementConfigurationStore } from '@app/municipality-management/services/municipality-management-configuration.store';
import { Strings } from '@app/shared/constants';
import { Region } from '@app/shared/services/api/regions.service';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { AuthService } from '@app/shared/services/auth.service';
import { StringUtility } from '@app/shared/string-utility';
import { isEqual, isNil } from 'lodash';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';
import { GemisTenant } from '@app/shared/enums';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';

export interface SelectableRegion extends Region {
  selected: boolean;
}

@Component({
  selector: 'app-municipality-information-tab',
  templateUrl: './municipality-information-tab.component.html',
  styleUrls: ['./municipality-information-tab.component.scss'],
})
export class MunicipalityInformationTabComponent implements OnInit, OnDestroy {
  private _subscriptions$: Subscription = new Subscription();
  private _unsubscribe$ = new Subject<boolean>();
  private prevMunicipality: string = '';

  public isSystemAdmin = true;
  public selectedRegion?: string;
  public municipalityKey?: string;
  public selectedRegionsArray: Region[] = [];

  public municipalityConfig$: Observable<MunicipalityStoreConfig> = of();
  public regionAssignment: SelectableRegion[] = [];

  public municipalityInformationForm: FormGroup = this.formBuilder.group({
    name: [{ value: '', disabled: !this.authService.isSystemAdmin }, Validators.required],
    zipCode: [{ value: '', disabled: !this.authService.isSystemAdmin }, Validators.compose([Validators.required, Validators.pattern('^[0-9]{5}')])],
    isDeactivated: [false],
  });

  get isGemisDSO(): boolean {
    return Object.values(GemisTenant).includes(environment.tenant as GemisTenant);
  }

  constructor(
    public authService: AuthService,
    private formBuilder: FormBuilder,
    private _configStore: MunicipalityManagementConfigurationStore,
    private readonly _appInsightsService: AppInsightsServiceDirective,
    public translate: TranslateService
  ) {}

  public ngOnDestroy(): void {
    this._subscriptions$.unsubscribe();
    this._unsubscribe$.next(true);
    this._unsubscribe$.complete();
  }

  ngOnInit() {
    this.isSystemAdmin = this.authService.isSystemAdmin;

    this.municipalityConfig$ = this._configStore.municipalityStoreConfiguration$.pipe(
      filter((config: MunicipalityStoreConfig) => !isNil(config.municipality)),
      distinctUntilChanged(
        (prev: MunicipalityStoreConfig, curr: MunicipalityStoreConfig) =>
          isEqual(prev.municipality, curr.municipality) && isEqual(prev.regionAssignment, curr.regionAssignment)
      ),
      tap((config: MunicipalityStoreConfig) => {
        this.municipalityInformationForm.patchValue(
          {
            name: config.municipality.name,
            zipCode: config.municipality.zipCode,
            isDeactivated: config.municipality.isDeactivated,
          },
          { emitEvent: false }
        );
        this.checkMunicipalityChange(config);
        this.tickExistingRegionAssignment(config);

        const selectedRegions = config.regionAssignment.selected;
        this.selectedRegion =
          selectedRegions && selectedRegions.length > 0
            ? selectedRegions.some((region) => StringUtility.areStringsIdentical(region, Strings.unassignedRegion))
              ? this.translate.instant('module.userProfile.generalInformation.selectAdminAreaPlaceholder')
              : selectedRegions.join(', ')
            : this.translate.instant('module.userProfile.generalInformation.selectAdminAreaPlaceholder');
      }),
      takeUntil(this._unsubscribe$)
    );

    this._subscriptions$.add(
      this.municipalityInformationForm.valueChanges
        .pipe(
          tap((formData) => {
            if (this.municipalityInformationForm.dirty) {
              const municipalityConfig = {
                ...formData,
                isActive: !formData.isDeactivated,
              };
              this._configStore.setMunicipalityConfig(municipalityConfig);
            }
            this._configStore.setFormValidity(this.municipalityInformationForm.valid);
          })
        )
        .subscribe()
    );
  }

  public selectRegion(region: Region): void {
    let selection = this.isGemisDSO ? this.toggleRegionSelection(region) : [region];

    if (selection.length === 0) {
      const unassigned = this.regionAssignment.find((available) => this.isUnassigned(available));

      if (!unassigned) {
        this._appInsightsService.logError(new Error(`No "${Strings.unassignedRegion}" region available to fall back to`), {
          context: 'municipality region assignment',
          municipalityGuid: this._configStore.municipalityConfig?.guid,
        });
      }

      selection = unassigned ? [unassigned] : [];
    }

    this.selectedRegionsArray = selection;

    this._configStore.setRegionAssignmentConfig({
      selected: this.selectedRegionsArray.map((selected) => selected.name),
      regionId: this.selectedRegionsArray.map((selected) => selected.guid),
    });
  }

  private toggleRegionSelection(region: Region): Region[] {
    const current = this.selectedRegionsArray ?? [];
    const isReselected = current.some((selected) => selected.guid === region.guid);

    if (isReselected) {
      return current.filter((selected) => selected.guid !== region.guid);
    }

    if (this.isUnassigned(region)) {
      return [region];
    }

    return [...current.filter((selected) => !this.isUnassigned(selected)), region];
  }

  private isUnassigned(region: Region): boolean {
    return StringUtility.areStringsIdentical(region.name, Strings.unassignedRegion);
  }

  private checkMunicipalityChange(config: MunicipalityStoreConfig): void {
    const newMunicipality = config.municipality.name;

    if (!this.prevMunicipality) {
      this.prevMunicipality = newMunicipality;
    } else if (newMunicipality !== this.prevMunicipality) {
      this.resetSelectedRegions();
      this.prevMunicipality = newMunicipality;
    }
  }

  private resetSelectedRegions(): void {
    this.selectedRegionsArray = [];
  }

  private tickExistingRegionAssignment(config: MunicipalityStoreConfig): void {
    const regionIds = config.regionAssignment.regionId;

    this.regionAssignment = config.regionAssignment.data.map((region: Region) => ({
      ...region,
      selected: regionIds.includes(region.guid),
    }));

    this.selectedRegionsArray = this.regionAssignment.filter((region) => region.selected);
  }

  public numberOnly(event: KeyboardEvent): boolean {
    const key = event.key;
    return key === 'Backspace' || key === 'Delete' || key === 'Tab' || (key >= '0' && key <= '9');
  }

  public deactivateChangeHandler(): void {
    this._configStore.setCanNavigateValue();
  }

  isFormInvalid(formName: string): boolean {
    const form = this.municipalityInformationForm.get(formName);
    return isNil(form) || (form.touched && form.invalid);
  }
}
